/**
 * Spec 0050 (U4) — per-tenant resource-limit PLAN assignment + effective-limits
 * display (saas-auth SASATH-0062).
 *
 * SUPER_ADMIN-only grouping on the Settings tab (beside the GHL section). The
 * caller (TenantSettings) gates rendering on `operator?.isSuperAdmin`; this
 * component additionally degrades to a contained "SUPER_ADMIN required" state if
 * the API returns 403. It self-fetches the product's PLANS + DEFINITIONS on
 * mount with isolated loading/empty/error/403 states — it is NOT folded into
 * tenantWorkspaceLoader, so an ordinary SAAS_ADMIN's Settings tab is never broken
 * by a 403 here (same rationale as the GHL section / 0044).
 *
 * CURRENT PLAN (SASADM-0051): the tenant's assigned plan is read from the
 * dedicated `tenantPlan.get(tenantId)` endpoint (`{ planId, planName }`, or null
 * when unassigned) — the revised SASATH-0062 hand-off that REPLACES 0050's assumed
 * `TenantDto.planId` read. `planName` is shown directly; `planId` resolves the
 * assigned plan's embedded `limits` for the effective-limits table. The assignment
 * is held in local state seeded from the fetch so assign/unassign reflect live
 * without re-reading.
 *
 * EFFECTIVE LIMITS are computed CLIENT-SIDE for DISPLAY CONVENIENCE only — the
 * product backend's server-to-server read is authoritative (stated in the UI
 * copy). The resolution is OVERRIDE-READY: it mirrors the backend precedence
 * `override ?? plan_value ?? default`. Per-tenant overrides are a SASATH-0062
 * Phase 2 non-goal, so the override term is reserved but never set here. A
 * definition with neither a plan value nor a default is OMITTED.
 */
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  LimitDefinition,
  LimitValueType,
  Plan,
  TenantPlanAssignment,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

type LimitSource = 'override' | 'plan' | 'default';

interface EffectiveLimit {
  definition: LimitDefinition;
  value: string;
  source: LimitSource;
}

/**
 * Resolve one definition's effective value, mirroring the backend precedence
 * `override ?? plan_value ?? default`. Returns null when NONE of the three is set
 * (the definition is then omitted from the display).
 *
 * OVERRIDE-READY (Non-Goal: no per-tenant overrides — SASATH-0062 Phase 2): the
 * `override` term is the first branch so a future per-tenant override drops in
 * without restructuring. It is always `undefined` here (never authored in this
 * UI). `null`/`undefined` (not the empty string) means "absent" for each term.
 */
function resolveEffectiveLimit(
  definition: LimitDefinition,
  planValue: string | undefined,
  override?: string | null,
): EffectiveLimit | null {
  if (override !== undefined && override !== null) {
    return { definition, value: override, source: 'override' };
  }
  if (planValue !== undefined && planValue !== null) {
    return { definition, value: planValue, source: 'plan' };
  }
  if (definition.defaultValue !== undefined && definition.defaultValue !== null) {
    return { definition, value: definition.defaultValue, source: 'default' };
  }
  return null;
}

/** Coerce a raw string value for DISPLAY per its value type (gap #2 → strings). */
function formatLimitValue(
  t: (key: string, fallback: string) => string,
  valueType: LimitValueType,
  raw: string,
): string {
  if (valueType === 'BOOL') {
    return raw === 'true'
      ? t('tenantPlan.boolEnabled', 'Enabled')
      : t('tenantPlan.boolDisabled', 'Disabled');
  }
  return raw;
}

function sourceLabel(
  t: (key: string, fallback: string) => string,
  source: LimitSource,
): string {
  if (source === 'override') return t('tenantPlan.source.override', 'Override');
  if (source === 'plan') return t('tenantPlan.source.plan', 'Plan');
  return t('tenantPlan.source.default', 'Default');
}

interface TenantPlanSectionProps {
  tenantId: number;
  productId: number | null;
}

export function TenantPlanSection({ tenantId, productId }: TenantPlanSectionProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  // `null` = not yet loaded; `[]` = loaded (possibly empty).
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [definitions, setDefinitions] = useState<LimitDefinition[]>([]);
  // The tenant's current plan assignment (`{ planId, planName }`, or null when
  // unassigned), seeded from the dedicated `tenantPlan.get` read on mount, then
  // owned locally so assign/unassign reflect live without re-reading.
  const [assignment, setAssignment] = useState<TenantPlanAssignment | null>(null);
  // The plan chosen in the assign dropdown (the option `value` is the stringified id).
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState(false);

  async function refresh() {
    if (productId === null) return;
    setLoadError(null);
    try {
      // All three reads are SUPER_ADMIN-gated; the effective-limits table needs
      // the definitions and the assigned plan's embedded limits, so fetch the
      // plans, the definitions, and the tenant's current-plan assignment together.
      const [planList, defList, currentAssignment] = await Promise.all([
        apiAdmin.resourceLimits.plans.list(productId),
        apiAdmin.resourceLimits.definitions.list(productId),
        apiAdmin.resourceLimits.tenantPlan.get(tenantId),
      ]);
      setPlans(planList);
      setDefinitions(defList);
      setAssignment(currentAssignment);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setPlans([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setPlans([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, tenantId]);

  async function handleAssign() {
    const id = Number(selectedPlanId);
    if (selectedPlanId === '' || Number.isNaN(id)) return;
    setBusy(true);
    try {
      await apiAdmin.resourceLimits.assignPlan(tenantId, id);
      // Reflect the assignment live: the chosen id comes from the plans dropdown,
      // so the plan (and its name for the chip) is always in the loaded list.
      const assigned = plans?.find((p) => p.id === id);
      setAssignment(assigned ? { planId: assigned.id, planName: assigned.name } : null);
      snack.success(t('tenantPlan.assigned', 'Plan assigned.'));
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleUnassign() {
    setBusy(true);
    try {
      await apiAdmin.resourceLimits.unassignPlan(tenantId);
      setAssignment(null);
      setSelectedPlanId('');
      snack.success(t('tenantPlan.unassigned', 'Plan removed.'));
      setConfirmUnassign(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmUnassign(false);
        return;
      }
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong.'));
      }
      setConfirmUnassign(false);
    } finally {
      setBusy(false);
    }
  }

  // Derived during render (no effect, no mirrored state): the assigned plan and
  // its effective limits. `assignment.planName` drives the current-plan chip;
  // `assignment.planId` resolves the plan in the loaded list for its embedded
  // limits. A definition with neither a plan value nor a default is omitted; the
  // override term is always undefined (Phase 2 non-goal).
  const currentPlanId = assignment?.planId ?? null;
  const currentPlan =
    currentPlanId !== null ? (plans?.find((p) => p.id === currentPlanId) ?? null) : null;
  const effectiveLimits: EffectiveLimit[] = definitions
    .map((def) => {
      const planValue = currentPlan?.limits.find((lv) => lv.definitionId === def.id)?.value;
      return resolveEffectiveLimit(def, planValue, undefined);
    })
    .filter((e): e is EffectiveLimit => e !== null);

  if (productId === null) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
        <Typography variant="h3" component="h3" gutterBottom>
          {t('tenantPlan.title', 'Resource-limit plan')}
        </Typography>
        <Alert severity="info">
          {t(
            'tenantPlan.noProduct',
            'Assign a product to this tenant before managing its resource-limit plan.',
          )}
        </Alert>
      </Paper>
    );
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
        <Typography variant="h3" component="h3" gutterBottom>
          {t('tenantPlan.title', 'Resource-limit plan')}
        </Typography>
        <Alert severity="warning">
          {t(
            'tenantPlan.superAdminRequired',
            'Super admin required to manage the resource-limit plan.',
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
      <Typography variant="h3" component="h3" gutterBottom>
        {t('tenantPlan.title', 'Resource-limit plan')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'tenantPlan.explanation',
          "Assign a resource-limit plan to grant this tenant the plan's values; a lever the plan leaves unset falls back to the definition default. The product backend's server-to-server read is authoritative — the effective limits below are a display convenience.",
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {plans === null ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <CircularProgress size={16} />
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading', 'Loading')}
          </Typography>
        </Stack>
      ) : (
        <>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('tenantPlan.currentPlan', 'Current plan')}:
              </Typography>
              {assignment ? (
                <Chip size="small" color="info" label={assignment.planName} />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('tenantPlan.noPlan', 'No plan assigned')}
                />
              )}
            </Stack>
            {plans.length === 0 ? (
              <Alert severity="info">
                {t(
                  'tenantPlan.noPlans',
                  'No plans are defined for this product yet. Create a plan on the product screen first.',
                )}
              </Alert>
            ) : (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  alignItems: { sm: 'center' },
                }}
              >
                <TextField
                  select
                  label={t('tenantPlan.selectLabel', 'Plan')}
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.active
                        ? p.name
                        : t('tenantPlan.inactiveOption', '{{name}} (inactive)', { name: p.name })}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  disabled={
                    busy || selectedPlanId === '' || Number(selectedPlanId) === currentPlanId
                  }
                  onClick={() => void handleAssign()}
                >
                  {t('tenantPlan.assign', 'Assign plan')}
                </Button>
                {currentPlanId !== null ? (
                  <Button color="error" disabled={busy} onClick={() => setConfirmUnassign(true)}>
                    {t('tenantPlan.unassign', 'Unassign plan')}
                  </Button>
                ) : null}
              </Stack>
            )}
          </Stack>

          <Typography variant="subtitle2" component="h4" gutterBottom>
            {t('tenantPlan.effectiveTitle', 'Effective limits')}
          </Typography>
          {effectiveLimits.length === 0 ? (
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(
                'tenantPlan.effectiveEmpty',
                'No effective limits — this product has no definitions with a plan value or a default.',
              )}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tenantPlan.columns.lever', 'Lever')}</TableCell>
                    <TableCell>{t('tenantPlan.columns.value', 'Effective value')}</TableCell>
                    <TableCell>{t('tenantPlan.columns.source', 'Source')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effectiveLimits.map((eff) => (
                    <TableRow key={eff.definition.id}>
                      <TableCell>
                        {eff.definition.label}
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                          }}
                        >
                          {eff.definition.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatLimitValue(t, eff.definition.valueType, eff.value)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={sourceLabel(t, eff.source)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirmUnassign}
        title={t('tenantPlan.unassignTitle', 'Unassign plan?')}
        body={t(
          'tenantPlan.unassignBody',
          "This removes the tenant's plan assignment. Its effective limits fall back to the definition defaults until a plan is assigned again.",
        )}
        severity="warning"
        confirmLabel={t('tenantPlan.unassign', 'Unassign plan')}
        busy={busy}
        onConfirm={() => void handleUnassign()}
        onCancel={() => setConfirmUnassign(false)}
      />
    </Paper>
  );
}
